import {createApp, h, ref, type App} from 'vue';
import BaseNotificationPopup from '@/components/BaseNotificationPopup.vue';
import BaseOverlay from '@/components/BaseOverlay.vue';
import {apiClient, isRedirectingToLogout} from '@/api/apiClient';
import {useTsdCancellationI18n} from './i18n';

export interface PayoutCancellationPopup {
    /**
     * The cancellation reached EMTA. Keeps the loading card up and polls `statusPath` until the
     * deferred actions have MTA's verdict, then reports success or MTA's own error text.
     * `onResolved` runs once the popup stops watching — on either outcome, and also when the user
     * dismisses a wait that never got an answer. The caller re-reads its grid (and takes its own
     * polling back) there.
     */
    awaitVerdict: (statusPath: string, onResolved?: () => void) => void;
    /** The cancellation never got to EMTA. Optional detail replaces the translated body text. */
    failed: (detail?: string) => void;
    /** Nothing to report — the delete needed no cancellation, or another surface has the answer. */
    close: () => void;
}

interface CancellationProgress {
    status: 'pending' | 'completed' | 'failed';
    errorMessage?: string;
}

const POLL_INTERVAL_MS = 5_000;

// MTA normally answers within a poll cycle or two (DeferredActions:IntervalSeconds, 30s by default).
// Past this the card stops claiming to be live and just lets the user out — the deferred actions
// finish the deletion whether anyone is watching or not.
const POLL_CEILING_MS = 3 * 60_000;

/**
 *  Imperative entrypoint for the legacy payouts pages. Deleting a paid-out row or a whole payout
 *  sheet can require cancelling lines EMTA already holds, which is a send like any other, so it
 *  gets the same popup the TSD report uses — mounted into a throwaway container because those
 *  pages are Razor and jQuery with no island around the grid.
 *
 *  Call it before the DELETE and drive the returned handle from the response:
 *      var popup = window.openPayoutCancellationPopup()
 *      ... 202 -> popup.awaitVerdict(path, refresh), 204 -> popup.close(), failure -> popup.failed(msg)
 *
 *  Mirrors openTsdIdent, except the lifecycle is show/settle rather than a promise: the caller
 *  reports an outcome into the popup and the user dismisses it.
 */
export function openPayoutCancellationPopup(
    options: {delayMs?: number; pollIntervalMs?: number} = {}
): PayoutCancellationPopup {
    const {t} = useTsdCancellationI18n();

    const variant = ref<'loading' | 'success' | 'error'>('loading');
    const title = ref(t('lbl-tsd-cancellation-pending'));
    const info = ref(t('lbl-tsd-cancellation-pending-info'));
    const dismissibleWhileLoading = ref(false);

    let app: App | null = null;
    let container: HTMLDivElement | null = null;
    let closed = false;
    let pollTimer = 0;
    let pendingResolution: (() => void) | null = null;

    // Ends the wait exactly once, whoever ended it: the verdict, or the user closing a card that
    // never got one. The caller's grid poll is suspended for the duration, so it has to hear about
    // both — a dismissed wait that stayed silent would leave the list frozen.
    function endWait() {
        window.clearInterval(pollTimer);
        const notify = pendingResolution;
        pendingResolution = null;
        if (notify) notify();
    }

    function close() {
        window.clearTimeout(timer);
        endWait();
        closed = true;
        if (!app) return;
        try {
            app.unmount();
        } catch {
            /* nothing to clean up */
        }
        container?.remove();
        app = null;
        container = null;
    }

    function actionLabel(): string {
        if (variant.value !== 'loading') return t('btn-close');
        return dismissibleWhileLoading.value ? t('btn-close') : '';
    }

    function mount() {
        if (app || closed) return;
        container = document.createElement('div');
        document.body.appendChild(container);
        app = createApp({
            name: 'PayoutCancellationPopupHost',
            render() {
                return h(BaseOverlay, null, {
                    default: () => h(
                        BaseNotificationPopup,
                        {
                            variant: variant.value,
                            title: title.value,
                            // Loading has nothing to dismiss until the wait runs long: it ends when
                            // MTA's verdict lands.
                            actionLabel: actionLabel(),
                            'data-test': 'payout-cancellation-popup',
                            onAction: close,
                        },
                        () => info.value,
                    ),
                });
            },
        });
        app.mount(container);
    }

    // Most deletes need no cancellation at all and come back 204 in milliseconds. Showing the card
    // straight away would flash "the cancellation has started" over every one of those, so the
    // loading state waits — a request that settles first mounts directly in its final state.
    const timer = window.setTimeout(mount, options.delayMs ?? 400);

    function settle(nextVariant: 'success' | 'error', nextTitle: string, nextInfo: string) {
        window.clearTimeout(timer);
        window.clearInterval(pollTimer);
        if (closed) return;
        variant.value = nextVariant;
        title.value = nextTitle;
        info.value = nextInfo;
        mount();
    }

    function reportSuccess() {
        settle('success', t('lbl-tsd-cancellation-succeeded'), t('lbl-tsd-cancellation-succeeded-info'));
    }

    function reportFailure(detail?: string) {
        settle('error', t('lbl-tsd-cancellation-failed'), detail || t('lbl-tsd-cancellation-failed-info'));
    }

    function awaitVerdict(statusPath: string, onResolved?: () => void) {
        // The cancellation is real and MTA is slow by nature, so the loader goes up now rather than
        // after the delay the 204 case needs.
        window.clearTimeout(timer);
        mount();

        pendingResolution = onResolved ?? (() => { /* nothing to re-read */ });
        const startedAt = Date.now();

        async function tick() {
            if (closed) {
                window.clearInterval(pollTimer);
                return;
            }
            // Session gone: apiClient is already navigating to logout, so stop instead of firing
            // 401s behind the redirect.
            if (isRedirectingToLogout()) {
                window.clearInterval(pollTimer);
                return;
            }

            let progress: CancellationProgress | null = null;
            try {
                progress = await apiClient.fetch<CancellationProgress>(statusPath);
            } catch {
                // A transient failure (or a request row not visible yet) is retried on the next tick;
                // the ceiling below is what ends an unanswered wait.
                progress = null;
            }

            if (progress && progress.status !== 'pending') {
                if (progress.status === 'completed') {
                    reportSuccess();
                } else {
                    reportFailure(progress.errorMessage);
                }
                endWait();
                return;
            }

            if (Date.now() - startedAt >= POLL_CEILING_MS) {
                window.clearInterval(pollTimer);
                dismissibleWhileLoading.value = true;
            }
        }

        pollTimer = window.setInterval(tick, options.pollIntervalMs ?? POLL_INTERVAL_MS);
    }

    return {awaitVerdict, failed: reportFailure, close};
}
